import type { CalendarEvent, Project } from "./entities";
import { createEvent, dayKey, dayStartOf, daysBetween, eventSpan, shiftDays } from "./events";
import { isLive, type Dataset, type OperationFactory, type SyncOperation } from "./operations";

/** Where the Delivery Event sits on the Deadline: the whole day, or a block of time on it. */
export type DeliveryPlan = { allDay: true } | { allDay: false; startMinutes: number; endMinutes: number };

export function deliveryTitle(projectName: string): string {
  return `Deliver ${projectName}`;
}

/** `minutes` after local midnight of a YYYY-MM-DD day, correct across daylight saving changes. */
function atMinutes(day: string, minutes: number): number {
  const date = new Date(dayStartOf(day));
  date.setHours(0, minutes, 0, 0);
  return date.getTime();
}

/** Plans the Delivery Event on a Project's Deadline and links it to the Project. */
export function planDelivery(project: Pick<Project, "id" | "name">, deadline: string, plan: DeliveryPlan, factory: OperationFactory): { id: string; operations: SyncOperation[] } {
  const start = plan.allDay ? dayStartOf(deadline) : atMinutes(deadline, plan.startMinutes);
  const end = plan.allDay ? shiftDays(start, 1) : Math.max(start, atMinutes(deadline, plan.endMinutes));
  const created = createEvent({
    title: deliveryTitle(project.name), notes: "", projectId: project.id, taskId: null, allDay: plan.allDay,
    start, end, startDay: plan.allDay ? deadline : null, endDay: plan.allDay ? deadline : null, repeat: null
  }, factory);
  return { id: created.id, operations: [...created.operations, factory.op("project", project.id, { deadlineEventId: created.id })] };
}

/** The Project's Delivery Event, while it is still on the Calendar. */
export function deliveryEvent(dataset: Dataset, project: Pick<Project, "deadlineEventId">): CalendarEvent | undefined {
  const event = project.deadlineEventId ? dataset.events[project.deadlineEventId] : undefined;
  return event && isLive(event) ? event : undefined;
}

/**
 * Sets or clears a Project's Deadline. The Delivery Event moves by as many days as the Deadline
 * did, keeping its time, and goes to Trash when the Deadline is cleared. One the User made repeat
 * is left where it is.
 */
export function changeDeadline(dataset: Dataset, project: Project, deadline: string | null, factory: OperationFactory, now: number): SyncOperation[] {
  const operations = [factory.op("project", project.id, deadline === null ? { deadline: null, deadlineEventId: null } : { deadline })];
  const event = deliveryEvent(dataset, project);
  if (!event) return operations;
  if (deadline === null) return [...operations, factory.op("event", event.id, { deletedAt: now })];
  if (event.repeat !== null || event.seriesId !== null) return operations;
  const span = eventSpan(event);
  const days = daysBetween(project.deadline ? dayStartOf(project.deadline) : span.start, dayStartOf(deadline));
  if (days === 0) return operations;
  if (event.allDay && event.startDay && event.endDay) {
    const startDay = dayKey(shiftDays(dayStartOf(event.startDay), days));
    const endDay = dayKey(shiftDays(dayStartOf(event.endDay), days));
    return [...operations, factory.op("event", event.id, { startDay, endDay, start: dayStartOf(startDay), end: shiftDays(dayStartOf(endDay), 1) })];
  }
  return [...operations, factory.op("event", event.id, { start: shiftDays(event.start, days), end: shiftDays(event.end, days) })];
}

/** Keeps the Delivery Event's title in step with a renamed Project, unless the User retitled it. */
export function renameDelivery(dataset: Dataset, project: Project, name: string, factory: OperationFactory): SyncOperation[] {
  const event = deliveryEvent(dataset, project);
  return event && event.title === deliveryTitle(project.name) ? [factory.op("event", event.id, { title: deliveryTitle(name) })] : [];
}

/** Whole days from today to the Deadline, negative once it has passed. Null without one, or once Delivered. */
export function daysToDeadline(project: Pick<Project, "deadline" | "completedAt">, now: number): number | null {
  if (project.deadline === null || project.completedAt !== null) return null;
  return daysBetween(now, dayStartOf(project.deadline));
}
